import { InputProps } from "../../../components/Input/Input";
import {
  AccountContactResponse,
  AccountCuitResponse,
  AccountTypeResponse,
  BeneficiaryAccountsResponse,
  CurrencyResponse,
  ExternalAccountResponse,
} from "../../../connectors/transactionsApi";
import {
  AccountContactData,
  AccountTypeData,
  BalanceType,
  Currency,
  ExternalAccountData,
  TransactionCurrencyData,
  UnifiedAccountData,
  UserAccount,
} from "../../../types/types";

export const contactStateDictionary = {
  1: "PENDING_APPROVED",
  2: "APPROVED",
  3: "REJECTED",
  5: "DELETED",
  4: "PENDING_DELETED",
};

export const contactAccountTypeDictionary = {
  "Cuenta Corriente": "CURRENT_ACCOUNT",
  "Caja Ahorro": "SAVING_ACCOUNT",
  "Billetera Virtual": "DIGITAL_WALLET",
  "Cuenta Exterior": "EXTERNAL_ACCOUNT",
};

export const reversedContactAccountTypeDictionary = {
  CURRENT_ACCOUNT: "Cuenta corriente",
  SAVING_ACCOUNT: "Caja de ahorro",
  DIGITAL_WALLET: "Billetera digital",
  EXTERNAL_ACCOUNT: "Cuenta Exterior",
};

const transactionCurrencyDictionary = {
  MEP_DOLAR_USD: "MEP_USD",
  PESO_AR: "ARS",
  DOLAR_USD: "CCL_USD",
};

export const getLabelCurrency = (item: "ARS" | "MEP_USD" | "CCL_USD") => {
  switch (item) {
    case "ARS":
      return "ARS";
      break;

    case "MEP_USD":
      return "MEP";
      break;

    case "CCL_USD":
      return "CCL";
      break;
  }
};

export const parseAccountContact = (response: AccountContactResponse) => {
  if (response?.success && response.data?.cbu) {
    return response.data.cbu.map((data) => ({
      id: data.id,
      bankAccountIdentification: data.cbu,
      currencyId: data.moneda.id,
      cuit: data.cuit_asociado,
      accountNumber: data.comitente.numero,
      accountType:
        contactAccountTypeDictionary[data.tipo_cuenta_bancaria.descripcion],
      nickName: data.referencia,
      state:
        contactStateDictionary[
          data.cbu_tipo_estado.id as keyof typeof contactStateDictionary
        ],
    })) as AccountContactData[];
  }

  return [];
};

export const parseExternalAccountContact = (
  response: ExternalAccountResponse
) => {
  if (response?.success && response.data?.cuenta_exterior) {
    return response.data.cuenta_exterior.map((data) => ({
      id: data.id,
      bankAccountIdentification: data.bank_address,
      currencyId: data.moneda.id,
      beneficiary: data.beneficiario,
      accountNumber: data.comitente.numero,
      nickName: data.bank_reference,
      state:
        contactStateDictionary[
          data.cuenta_exterior_tipo_estado
            .id as keyof typeof contactStateDictionary
        ],
    })) as ExternalAccountData[];
  }

  return [];
};

export const parseUnifiedAccount = (
  response: ExternalAccountResponse | AccountContactResponse
) => {
  if (response?.success) {
    // cxs
    if ("cbu" in response?.data) {
      return response?.data?.cbu.map((data) => ({
        id: data.id,
        bankAccountIdentification: data.cbu,
        currencyId: data.moneda.id,
        cuit: data.cuit_asociado,
        accountNumber: data.comitente.numero,
        accountType:
          contactAccountTypeDictionary[data.tipo_cuenta_bancaria.descripcion],
        nickName: data.referencia,
        state:
          contactStateDictionary[
            data.cbu_tipo_estado.id as keyof typeof contactStateDictionary
          ],
      })) as UnifiedAccountData[];
    }

    // external accounts
    if ("cuenta_exterior" in response?.data) {
      return response?.data?.cuenta_exterior.map((data) => ({
        id: data.id,
        bankAccountIdentification: data.bank_address,
        currencyId: data.moneda.id,
        cuit: 0,
        accountNumber: data.comitente.numero,
        accountType: "EXTERNAL_ACCOUNT",
        nickName: data.bank_reference,
        state:
          contactStateDictionary[
            data.cuenta_exterior_tipo_estado
              .id as keyof typeof contactStateDictionary
          ],
        external: {
          singleBank: data.bank,
          singleBankNumber: data.bank_address,
          singleBankSwift: data.bank_swift,
          singleBankABA: data.bank_aba,
          singleBankReference: data.bank_reference,
          isSingleBank: data.requiere_banco_intermediario == 0,
          intermediaryBank: data.intermediary_bank,
          intermediaryBankSwift: data.intermediary_bank_swift,
          intermediaryBankABA: data.intermediary_bank_aba,
          intermediaryBankReference: data.intermediary_bank_reference,
        },
      })) as UnifiedAccountData[];
    }
  }

  return [];
};

export const parseCurrency = (response: CurrencyResponse) => {
  if (response?.success && response.data?.moneda) {
    return response.data.moneda.map((data) => ({
      id: data.id,
      description: transactionCurrencyDictionary[data.codigo],
    })) as TransactionCurrencyData[];
  }
  return [];
};

export const parseAccountType = (response: AccountTypeResponse) => {
  if (response?.success && response.data?.tipo_cuenta_bancaria) {
    return response.data.tipo_cuenta_bancaria.map((data) => ({
      id: data.id,
      description: contactAccountTypeDictionary[data.descripcion],
      parseDescription:
        reversedContactAccountTypeDictionary[
          contactAccountTypeDictionary[
            data.descripcion
          ] as keyof typeof reversedContactAccountTypeDictionary
        ],
    })) as AccountTypeData[];
  }
  return [];
};

export const parseAccountCuit = (response: AccountCuitResponse) => {
  if (response?.success && response.data?.cuit_asociado) {
    return response.data.cuit_asociado.map((cuit) => cuit);
  }
  return [];
};

export const parseBeneficiaryAccounts = (
  response: BeneficiaryAccountsResponse
) => {
  if (response?.success && response.data?.beneficiarios) {
    return response.data.beneficiarios.map(
      (beneficiario) => beneficiario.cuit_comitente
    );
  }
};

export const getCurrencyByID = (id: number | string) =>
  id == 1 ? "ARS" : id == 3 ? "CCL_USD" : "MEP_USD";

const CURRENCY_IDS: Record<string, number> = {
  ARS: 1,
  MEP_USD: 2,
  CCL_USD: 3,
};

export const getCurrencyID = (currency: string): number => {
  return CURRENCY_IDS[currency] ?? 1; // default: ARS
};

export const parsedBalances = (
  accountBalances: UserAccount["balances"],
  balanceType: BalanceType
): { [key in Currency]: number } => {
  const getQty = (currency: Currency) =>
    accountBalances
      ?.find((b) => b.currency === currency)
      ?.balancesTypes.find((t) => t.type === balanceType)
      ?.settlement_quantity ?? 0;

  return {
    ARS: getQty("ARS"),
    MEP_USD: getQty("MEP_USD"),
    CCL_USD: getQty("CCL_USD"),
  };
};

export const parsedAvailableBalances = (
  accountBalances: UserAccount["balances"],
  balanceType: BalanceType
): { [key in Currency]: number } => {
  const getQty = (currency: Currency) =>
    accountBalances
      ?.find((b) => b.currency === currency)
      ?.balancesTypes.find((t) => t.type === balanceType)
      ?.available_quantity ?? 0;

  return {
    ARS: getQty("ARS"),
    MEP_USD: getQty("MEP_USD"),
    CCL_USD: getQty("CCL_USD"),
  };
};

export const labelsForCurrency: { [key in Currency]: string } = {
  ARS: "ARS",
  MEP_USD: "MEP",
  CCL_USD: "CCL",
};

export const currenciesForInput: { [key in Currency]: InputProps["currency"] } =
  {
    ARS: "ARS",
    MEP_USD: "USD",
    CCL_USD: "USD",
  };

export const symbolsForCurrency: { [key in Currency]: InputProps["currency"] } =
  {
    ARS: "$",
    MEP_USD: "U$S",
    CCL_USD: "U$S",
  };
